/**
 * PlaceNotesEditor — "Notes for drivers" on a site, well or disposal facility (#45 PR 2,
 * DEC-359). Drivers see the note read-only on their phone; it is never printed or shown to
 * customers.
 *
 * The note is kept exactly as typed (no uppercasing — DEC-147 notes exception), line breaks
 * included, up to 2,000 characters. Saving sends the `updatedAt` this editor loaded: if
 * someone changed the note in between, the API refuses and the editor offers to reload
 * rather than overwrite their change. Clearing deletes the note. Without the catalog
 * permission (`canManage`, UX only — the API is authoritative) the note is read-only.
 */

import * as React from 'react';
import type { QueryKey } from '@tanstack/react-query';
import {
  MAX_NOTE_LENGTH,
  StaleNoteError,
  usePlaceNote,
  useSavePlaceNote,
  type PlaceKind,
} from './place-notes';

const subText: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

/** Local time, as the site and well audit trails show it (no UTC shown to users). */
function fmtChangedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
const btn: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: '13px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-secondary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  ...btn,
  backgroundColor: 'var(--color-accent)',
  borderColor: 'var(--color-accent)',
  color: 'var(--color-text-primary)',
};

export function PlaceNotesEditor({
  kind,
  id,
  placeName,
  canManage,
  alsoInvalidate = [],
}: {
  readonly kind: PlaceKind;
  readonly id: string;
  readonly placeName: string;
  readonly canManage: boolean;
  /** Queries that show this change too (the site or well audit trail). */
  readonly alsoInvalidate?: readonly QueryKey[];
}): React.JSX.Element {
  const { data, isLoading, isError, refetch } = usePlaceNote(kind, id);
  const save = useSavePlaceNote(alsoInvalidate);
  const loaded = data?.note ?? null;
  const [draft, setDraft] = React.useState<string | null>(null);

  // A fresh load (first open, a reload, or someone else's change) resets the draft.
  React.useEffect(() => {
    setDraft(null);
  }, [loaded?.updatedAt]);

  if (isLoading) return <p style={subText}>Loading notes…</p>;
  if (isError)
    return (
      <p style={{ ...subText, color: 'var(--color-danger)' }}>Couldn&apos;t load the notes.</p>
    );

  const text = draft ?? loaded?.notes ?? '';
  const dirty = draft !== null && draft.trim() !== (loaded?.notes ?? '');
  const tooLong = text.length > MAX_NOTE_LENGTH;
  const stale = save.error instanceof StaleNoteError;

  const submit = (notes: string | null): void => {
    save.mutate(
      { target: { kind, id }, notes, expectedUpdatedAt: loaded?.updatedAt ?? null },
      { onSuccess: () => setDraft(null) },
    );
  };

  return (
    <section aria-label={`Notes for drivers at ${placeName}`}>
      <p style={{ ...subText, marginBottom: '6px' }}>
        Drivers see these notes on their phone. They are never printed or shown to customers.
      </p>
      {canManage ? (
        <>
          <textarea
            aria-label="Notes for drivers"
            value={text}
            rows={4}
            onChange={(e): void => {
              save.reset();
              setDraft(e.target.value);
            }}
            placeholder="e.g. Gate code 4417. Call the pumper before entry."
            style={{
              width: '100%',
              fontSize: '14px',
              padding: '8px',
              borderRadius: '6px',
              border: `1px solid ${tooLong ? 'var(--color-danger)' : 'var(--color-border-secondary)'}`,
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-primary)',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              marginTop: '6px',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              style={primaryBtn}
              disabled={!dirty || tooLong || save.isPending}
              onClick={(): void => submit(text.trim() === '' ? null : text)}
            >
              {save.isPending ? 'Saving…' : 'Save notes'}
            </button>
            {loaded !== null && (
              <button
                type="button"
                style={btn}
                disabled={save.isPending}
                onClick={(): void => submit(null)}
              >
                Clear
              </button>
            )}
            {dirty && (
              <button type="button" style={btn} onClick={(): void => setDraft(null)}>
                Undo changes
              </button>
            )}
            <span
              style={{
                ...subText,
                marginLeft: 'auto',
                color: tooLong ? 'var(--color-danger)' : subText.color,
              }}
              data-testid="note-counter"
            >
              {String(text.length)} / {String(MAX_NOTE_LENGTH)}
            </span>
          </div>
        </>
      ) : (
        <p style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{loaded?.notes ?? 'No notes.'}</p>
      )}
      {loaded !== null && (
        <p style={{ ...subText, marginTop: '6px' }}>
          Last changed by {loaded.updatedByName ?? 'someone'}, {fmtChangedAt(loaded.updatedAt)}
        </p>
      )}
      {save.isError && (
        <p role="alert" style={{ ...subText, color: 'var(--color-danger)', marginTop: '6px' }}>
          {save.error.message}{' '}
          {stale && (
            <button
              type="button"
              style={btn}
              onClick={(): void => {
                save.reset();
                setDraft(null);
                void refetch();
              }}
            >
              Reload
            </button>
          )}
        </p>
      )}
    </section>
  );
}

/**
 * The read-only notes block for the job detail (DEC-359 choice 9). Renders nothing when
 * the place has no note, or the note can't be read.
 */
export function PlaceNoteReadOnly({
  label,
  kind,
  id,
}: {
  readonly label: string;
  readonly kind: PlaceKind;
  readonly id: string | null;
}): React.JSX.Element | null {
  const { data } = usePlaceNote(kind, id);
  const note = data?.note ?? null;
  if (id === null || note === null) return null;
  return (
    <div style={{ marginTop: '8px' }}>
      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
        {label}
      </span>
      <p style={{ whiteSpace: 'pre-wrap', fontSize: '14px', margin: '2px 0 0' }}>{note.notes}</p>
    </div>
  );
}
